import { StyledSelect } from "@ando/ui";
/**
 * SetupWizard — DSN registration UI for the on-prem DB bridge.
 *
 * Three ordered steps inside ONE Dialog (modal, per memory rule
 * ``feedback_prefer_modals_over_side_drawers``):
 *
 *   1. Dialect — the user picks the DB engine BEFORE typing any
 *      credentials (memory rule ``feedback_action_forms_account_picker_first``).
 *      Different dialects imply different default ports and connection
 *      quirks, so committing here first avoids the cascade of dependent
 *      re-renders that a "credentials-first" flow would trigger.
 *
 *   2. Credentials — host / port / database / user / password. Values live
 *      in local state ONLY; they never touch the cloud API or the OS
 *      keychain yet. The primary CTA on this step mints a THROWAWAY test
 *      handle via ``crypto.randomUUID()`` (valid UUIDv4 matching the Rust
 *      ``validate_handle`` shape check — it only keys the Rust pool cache)
 *      and calls ``dbExecute({ operation: "check_connection", dsn: <inline> })``
 *      so Rust builds a pool for the just-entered credentials without any
 *      keychain entry existing yet. On failure we surface the driver
 *      error and let the user amend before advancing.
 *
 *   3. Confirm — the user names the connector (display_name) and clicks
 *      "Salva". Under the hood:
 *        a. ``dbRegisterDsn(dsn)`` — Rust writes the DSN into the OS
 *           keychain (``keyring`` crate) and mints the REAL
 *           ``bridge_device_handle``. Keychain-first so a BE crash
 *           between our POST and its 201 can't leave a live BE row with
 *           no local DSN behind it.
 *        b. POST ``/enterprise-connectors/bridge`` with
 *           ``{ dialect, bridge_device_handle, display_name }``.
 *        c. On BE failure roll back with ``dbDeleteDsn(handle)`` (keychain
 *           entry + Rust pool cache) so the canonical answer to "is this
 *           handle valid" is a single "no" across keychain + BE row.
 *        d. Evict the step-2 test pool (``dbDeleteDsn(testHandle)``) —
 *           the registered handle differs, so that pool is now an orphan.
 *
 * Zero-DSN commitment: at NO point does the DB password leave the local
 * machine. JS holds it only as this wizard's in-memory form state; the
 * cloud only ever sees the opaque handle.
 *
 * All strings via Lingui (per memory rule
 * ``feedback_client_ui_strings_must_use_lingui``). Lucide icons only
 * (memory rule ``feedback_no_emoji_in_user_facing_strings``).
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FC,
  type ReactElement,
} from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiClient } from "@ando/core/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@ando/ui";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronRight,
  Database,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Settings2,
  ShieldCheck,
  TriangleAlert,
} from "lucide-react";

import {
  dbDeleteDsn,
  dbExecute,
  dbRegisterDsn,
  isEnterpriseDbBridgeAvailable,
} from "./dbBridge";
import type {
  BridgeConnectorCreateRequest,
  BridgeConnectorCreateResponse,
  DsnSpec,
  EnterpriseDbDialect,
} from "./types";

// ── Wizard shape ────────────────────────────────────────────────────

type WizardStep = 1 | 2 | 3;

interface DialectOption {
  readonly value: EnterpriseDbDialect;
  readonly defaultPort: number;
}

/**
 * Dialects the sqlx bundle on the desktop supports. Order here drives
 * the picker order in step 1 — Postgres first (the most common on-prem
 * ERP backend in the Italian midmarket) then descending frequency.
 */
const DIALECTS: readonly DialectOption[] = [
  { value: "postgresql", defaultPort: 5432 },
  { value: "mysql", defaultPort: 3306 },
  { value: "mssql", defaultPort: 1433 },
  { value: "oracle", defaultPort: 1521 },
] as const;

/**
 * Human-facing labels — module scope because these are brand names, not
 * translatable copy (memory rule ``feedback_client_ui_strings_must_use_lingui``
 * exempts trademarks). Hoisted out of the component to skip the useless
 * per-render allocation ``useMemo(..., [])`` produced.
 */
const DIALECT_LABELS: Record<EnterpriseDbDialect, string> = {
  postgresql: "PostgreSQL",
  mysql: "MySQL",
  mssql: "SQL Server",
  oracle: "Oracle",
};

interface CredentialsState {
  host: string;
  port: string;
  database: string;
  user: string;
  password: string;
}

const EMPTY_CREDENTIALS: CredentialsState = {
  host: "",
  port: "",
  database: "",
  user: "",
  password: "",
};

// ── Advanced options (dialect-specific tuning) ──────────────────────
//
// Every knob is stored as its raw string form (the shape the DSN
// ``options`` map ultimately carries on the wire) so no per-dialect
// state discriminated union is needed at rest. ``buildOptionsFromState``
// projects the state down to the ``Record<string, string>`` the Rust
// side expects, DROPPING keys the user left at their default or blank
// so a wizard run with the panel untouched keeps the same wire shape
// as before this section existed (backwards-compat, per task brief).

/** Postgres ``sslmode`` values sqlx accepts (libpq semantics). */
const PG_SSLMODES = [
  "disable",
  "prefer",
  "require",
  "verify-ca",
  "verify-full",
] as const;
type PgSslMode = (typeof PG_SSLMODES)[number];
const PG_SSLMODE_DEFAULT: PgSslMode = "prefer";

/** MSSQL ``Encrypt`` values the driver understands. */
const MSSQL_ENCRYPT = ["true", "false", "Optional"] as const;
type MssqlEncrypt = (typeof MSSQL_ENCRYPT)[number];
const MSSQL_ENCRYPT_DEFAULT: MssqlEncrypt = "true";

/** MySQL ``sslMode`` (the connector-j / sqlx-mysql canonical names). */
const MYSQL_SSLMODES = [
  "DISABLED",
  "PREFERRED",
  "REQUIRED",
  "VERIFY_CA",
  "VERIFY_IDENTITY",
] as const;
type MysqlSslMode = (typeof MYSQL_SSLMODES)[number];
const MYSQL_SSLMODE_DEFAULT: MysqlSslMode = "PREFERRED";

interface AdvancedOptionsState {
  // Postgres
  pgSslMode: PgSslMode;
  pgSslRootCert: string;
  pgConnectTimeout: string; // seconds, blank = default (10)
  // MSSQL
  mssqlTrustServerCertificate: boolean;
  mssqlEncrypt: MssqlEncrypt;
  mssqlConnectionTimeout: string; // seconds, blank = default (15)
  // MySQL
  mysqlSslMode: MysqlSslMode;
  mysqlConnectTimeout: string; // milliseconds, blank = default (10000)
  // Oracle
  oracleServiceName: string;
  oracleConnectTimeout: string; // seconds, blank = default (30)
}

const EMPTY_ADVANCED_OPTIONS: AdvancedOptionsState = {
  pgSslMode: PG_SSLMODE_DEFAULT,
  pgSslRootCert: "",
  pgConnectTimeout: "",
  mssqlTrustServerCertificate: false,
  mssqlEncrypt: MSSQL_ENCRYPT_DEFAULT,
  mssqlConnectionTimeout: "",
  mysqlSslMode: MYSQL_SSLMODE_DEFAULT,
  mysqlConnectTimeout: "",
  oracleServiceName: "",
  oracleConnectTimeout: "",
};

/**
 * True when ``raw`` is a positive-integer decimal (no sign, no dot, no
 * exponent). Blank strings are treated as "unset" and validated as OK —
 * ``buildOptionsFromState`` drops them from the emitted map.
 */
function isPositiveIntOrBlank(raw: string): boolean {
  if (raw.length === 0) return true;
  if (!/^\d+$/.test(raw)) return false;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0;
}

/**
 * True when every advanced-options field the user has filled in for
 * ``dialect`` passes its per-field validator. Fields belonging to a
 * different dialect are ignored (we only emit the current dialect's
 * options at submit time).
 */
function isAdvancedOptionsValid(
  dialect: EnterpriseDbDialect,
  state: AdvancedOptionsState,
): boolean {
  switch (dialect) {
    case "postgresql":
      if (!PG_SSLMODES.includes(state.pgSslMode)) return false;
      if (!isPositiveIntOrBlank(state.pgConnectTimeout)) return false;
      return true;
    case "mssql":
      if (!MSSQL_ENCRYPT.includes(state.mssqlEncrypt)) return false;
      if (!isPositiveIntOrBlank(state.mssqlConnectionTimeout)) return false;
      return true;
    case "mysql":
      if (!MYSQL_SSLMODES.includes(state.mysqlSslMode)) return false;
      if (!isPositiveIntOrBlank(state.mysqlConnectTimeout)) return false;
      return true;
    case "oracle":
      if (!isPositiveIntOrBlank(state.oracleConnectTimeout)) return false;
      return true;
  }
}

/**
 * Project the AdvancedOptionsState into the ``Record<string, string>``
 * the Rust ``DsnSpec.options`` map expects. Only keys the user has
 * ACTIVELY set (non-default, non-blank) land in the map so a wizard run
 * with the panel untouched yields an empty map — the caller can then
 * omit the ``options`` field entirely and keep the pre-existing wire
 * shape (backwards-compat).
 *
 * Field-name casing mirrors the DSN parameters each driver documents so
 * Rust can pass them straight through to sqlx / the DB URL builder
 * without a rename table.
 */
function buildOptionsFromState(
  dialect: EnterpriseDbDialect,
  state: AdvancedOptionsState,
): Record<string, string> {
  const out: Record<string, string> = {};
  switch (dialect) {
    case "postgresql": {
      if (state.pgSslMode !== PG_SSLMODE_DEFAULT) {
        out.sslmode = state.pgSslMode;
      }
      // sslrootcert only makes sense with verify-{ca,full}; the UI hides
      // the field otherwise, but guard here too so a stale value from a
      // prior sslmode selection doesn't leak into the wire map.
      if (
        state.pgSslRootCert.trim().length > 0 &&
        (state.pgSslMode === "verify-ca" || state.pgSslMode === "verify-full")
      ) {
        out.sslrootcert = state.pgSslRootCert.trim();
      }
      if (state.pgConnectTimeout.trim().length > 0) {
        out.connect_timeout = state.pgConnectTimeout.trim();
      }
      return out;
    }
    case "mssql": {
      if (state.mssqlTrustServerCertificate) {
        out.TrustServerCertificate = "true";
      }
      if (state.mssqlEncrypt !== MSSQL_ENCRYPT_DEFAULT) {
        out.Encrypt = state.mssqlEncrypt;
      }
      if (state.mssqlConnectionTimeout.trim().length > 0) {
        out.ConnectionTimeout = state.mssqlConnectionTimeout.trim();
      }
      return out;
    }
    case "mysql": {
      if (state.mysqlSslMode !== MYSQL_SSLMODE_DEFAULT) {
        out.sslMode = state.mysqlSslMode;
      }
      if (state.mysqlConnectTimeout.trim().length > 0) {
        out.connectTimeout = state.mysqlConnectTimeout.trim();
      }
      return out;
    }
    case "oracle": {
      if (state.oracleServiceName.trim().length > 0) {
        out.service_name = state.oracleServiceName.trim();
      }
      if (state.oracleConnectTimeout.trim().length > 0) {
        out.connect_timeout = state.oracleConnectTimeout.trim();
      }
      return out;
    }
  }
}

// ── Props ───────────────────────────────────────────────────────────

export interface EnterpriseDbBridgeSetupWizardProps {
  readonly open: boolean;
  readonly onOpenChange: (next: boolean) => void;
  /**
   * Fired once the BE row is created — the parent (usually a connectors
   * listing) can refetch its query to surface the new bridge.
   */
  readonly onRegistered?: (row: BridgeConnectorCreateResponse) => void;
  /**
   * Pre-select the DB engine when the wizard opens so the user lands
   * directly on step 2 (credentials). Populated by the Integrations page
   * when the user clicks a specific database card (e.g. "Microsoft SQL
   * Server") — the picker step is only useful when the caller can't
   * name the dialect upfront. Undefined restores the historical
   * behaviour (step 1 picker first). Reset alongside the rest of the
   * form on dialog reopen so a new open with a different ``initialDialect``
   * always wins.
   */
  readonly initialDialect?: EnterpriseDbDialect;
}

// ── Shared classes ──────────────────────────────────────────────────

const inputCls =
  "w-full rounded-md border border-hairline bg-card px-2.5 py-1.5 text-sm text-ink-1 placeholder:text-ink-3 focus:outline-none focus:ring-2 focus:ring-ring";

// ── Component ───────────────────────────────────────────────────────

export const EnterpriseDbBridgeSetupWizard: FC<
  EnterpriseDbBridgeSetupWizardProps
> = ({ open, onOpenChange, onRegistered, initialDialect }) => {
  const { t } = useLingui();

  const [step, setStep] = useState<WizardStep>(initialDialect ? 2 : 1);
  const [dialect, setDialect] = useState<EnterpriseDbDialect | null>(
    initialDialect ?? null,
  );
  const [credentials, setCredentials] = useState<CredentialsState>(EMPTY_CREDENTIALS);
  const [displayName, setDisplayName] = useState("");
  // Throwaway handle that keys the Rust pool cache for the step-2
  // inline-DSN test. NOT the registered ``bridge_device_handle`` — Rust
  // mints that in step 3 (``dbRegisterDsn``) and it never lives in
  // React state beyond the POST body.
  const [testHandle, setTestHandle] = useState<string | null>(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [savingBe, setSavingBe] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [testedOk, setTestedOk] = useState(false);
  // Set to true the instant the BE POST returns 2xx — flips the dismiss
  // path from "orphan cleanup" to "plain close" (the test pool has
  // already been evicted by the save path at that point).
  const [savedToBe, setSavedToBe] = useState(false);
  // Non-null while an AlertDialog is asking the user to confirm dismissing
  // a dirty wizard. Keeps the primary Dialog open — Radix only closes on
  // explicit ``onOpenChange(false)``.
  const [confirmingDismiss, setConfirmingDismiss] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [advancedOptions, setAdvancedOptions] =
    useState<AdvancedOptionsState>(EMPTY_ADVANCED_OPTIONS);

  const desktopAvailable = isEnterpriseDbBridgeAvailable();

  // Reset form whenever the dialog re-opens fresh so prior state doesn't
  // leak across attempts (same pattern as ``CreateTemplateDialog``). When
  // the parent hands us an ``initialDialect`` (Integrations page clicked
  // a specific engine card), skip the picker and land straight on step 2
  // with that dialect selected. Otherwise fall back to the historical
  // picker-first flow.
  useEffect(() => {
    if (open) {
      setStep(initialDialect ? 2 : 1);
      setDialect(initialDialect ?? null);
      setCredentials(EMPTY_CREDENTIALS);
      setDisplayName("");
      setTestHandle(null);
      setTestingConnection(false);
      setSavingBe(false);
      setErrorMessage(null);
      setTestedOk(false);
      setSavedToBe(false);
      setConfirmingDismiss(false);
      setShowPw(false);
      setAdvancedOpen(false);
      setAdvancedOptions(EMPTY_ADVANCED_OPTIONS);
    }
  }, [open, initialDialect]);

  // Auto-populate the default port when the user picks a new dialect so
  // the credentials step feels one-tap for the common case.
  useEffect(() => {
    if (!dialect) return;
    const option = DIALECTS.find((d) => d.value === dialect);
    if (!option) return;
    setCredentials((prev) =>
      prev.port === "" ? { ...prev, port: String(option.defaultPort) } : prev,
    );
  }, [dialect]);

  // Unmount safety net — if the parent tears the wizard down mid-flight
  // (route change, feature-flag toggle, error boundary) we still need to
  // evict the Rust-side sqlx pool that our step-2 test-connection may have
  // opened under `testHandle`. No keychain cleanup is needed here: step-2
  // never registers anything (that only happens on save-to-BE in step 3,
  // under a different, Rust-minted handle), so the test handle has no
  // keychain entry to purge. Refs (not deps) so we always read the latest
  // values without re-registering the teardown, and the effect fires
  // exactly once on unmount rather than on every prop tick.
  const testHandleRef = useRef(testHandle);
  const savedToBeRef = useRef(savedToBe);
  useEffect(() => {
    testHandleRef.current = testHandle;
  }, [testHandle]);
  useEffect(() => {
    savedToBeRef.current = savedToBe;
  }, [savedToBe]);
  useEffect(() => {
    return () => {
      const handle = testHandleRef.current;
      if (handle && !savedToBeRef.current) {
        // Pool-cache eviction only — the save path already evicted it
        // when ``savedToBe`` is true. Best-effort by design.
        void dbDeleteDsn(handle);
      }
    };
  }, []);

  // Port must be a real integer in the IANA registered range — 0 is the
  // wildcard, ≥65536 doesn't fit a uint16, and decimals silently truncate
  // in most drivers and yield "connection refused" on a wrong port later.
  const port = Number(credentials.port);
  const portOk = Number.isInteger(port) && port >= 1 && port <= 65535;

  const credentialsReady =
    dialect !== null &&
    credentials.host.trim().length > 0 &&
    credentials.port.trim().length > 0 &&
    portOk &&
    credentials.database.trim().length > 0 &&
    credentials.user.trim().length > 0 &&
    credentials.password.length > 0 &&
    isAdvancedOptionsValid(dialect, advancedOptions);

  // "Dirty" == the user typed something or we already ran a test. If
  // either is true, dismissing should confirm; otherwise close silently.
  const isDirty =
    testHandle !== null ||
    credentials.host.length > 0 ||
    credentials.port.length > 0 ||
    credentials.database.length > 0 ||
    credentials.user.length > 0 ||
    credentials.password.length > 0 ||
    displayName.length > 0;

  // Actually close: fire the parent callback and, if we opened a test pool
  // that the save path hasn't already evicted, evict the Rust-side sqlx
  // pool so a subsequent wizard run for the same host doesn't reuse a
  // stale pool. NO keychain entry to delete here — step-2 never registers
  // anything; only step-3 does, under the Rust-minted handle the BE now
  // owns. ``dbDeleteDsn`` is best-effort and no-ops off-desktop, so this
  // is safe to always await-less-invoke.
  const closeAndCleanup = useCallback((): void => {
    if (testHandle && !savedToBe) {
      void dbDeleteDsn(testHandle);
    }
    onOpenChange(false);
  }, [testHandle, savedToBe, onOpenChange]);

  // Intercept Radix' ``onOpenChange`` so ``X`` / Esc / outside-click can't
  // silently drop unsaved credentials or leak a test pool. Opens are
  // always forwarded; closes on a dirty wizard trip the confirm.
  const handleOpenChange = useCallback(
    (next: boolean): void => {
      if (next) {
        onOpenChange(true);
        return;
      }
      if (!isDirty || savedToBe) {
        closeAndCleanup();
        return;
      }
      setConfirmingDismiss(true);
    },
    [isDirty, savedToBe, onOpenChange, closeAndCleanup],
  );

  // Assemble the DSN from form state. ONE builder shared by the step-2
  // test and the step-3 registration so what Rust stores in the keychain
  // matches EXACTLY the DSN we just proved reachable — any drift would
  // surface later as a keychain-built pool with different tuning knobs
  // than the one that succeeded in step 2.
  //
  // Only include ``options`` when the advanced panel produced >=1 entry —
  // an empty map would still be truthy on the wire and force the Rust
  // side to re-derive its pool key under a different hash than a same-run
  // without options, so we keep the omit-on-empty pattern that mirrors
  // the pre-advanced-options behaviour.
  function buildDsn(forDialect: EnterpriseDbDialect): DsnSpec {
    const options = buildOptionsFromState(forDialect, advancedOptions);
    return {
      dialect: forDialect,
      host: credentials.host.trim(),
      port,
      database: credentials.database.trim(),
      user: credentials.user.trim(),
      password: credentials.password,
      ...(Object.keys(options).length > 0 ? { options } : {}),
    };
  }

  // Test-with-inline-DSN — see file docstring §2. Mints a throwaway test
  // handle if we don't have one yet (retries within the same wizard reuse
  // the same handle so the Rust pool cache doesn't fill up with test-only
  // entries), then hands the just-entered credentials to Rust INLINE via
  // ``dbExecute({..., dsn})``. Nothing lands in the OS keychain yet —
  // that only happens on save-to-BE below, under a Rust-minted handle.
  async function handleTestAndRegister(): Promise<void> {
    if (!credentialsReady || !dialect) return;
    setErrorMessage(null);
    setTestingConnection(true);
    setTestedOk(false);

    // Reuse the current handle across retries within one wizard session so
    // a series of failed attempts doesn't leave 5+ pool-cache entries
    // pinning connections. ``crypto.randomUUID()`` emits a lowercase
    // RFC-4122 UUIDv4 which matches the Rust ``validate_handle`` shape
    // (dashes at 8/13/18/23, version nibble '4', variant nibble 8-b) —
    // Rust validates the shape even when the DSN rides inline.
    const handle = testHandle ?? crypto.randomUUID();
    const dsn = buildDsn(dialect);

    try {
      // Reasonable ceiling for a "just checking" round trip — the real
      // per-query timeout is set by the BE on each frame it sends.
      await dbExecute({
        bridge_device_handle: handle,
        operation: "check_connection",
        timeout_ms: 10_000,
        dsn,
      });
      setTestHandle(handle);
      setTestedOk(true);
      setStep(3);
    } catch (err) {
      // Test failed — the Rust side may have cached a broken pool for
      // this handle (e.g. wrong host reachable but bad creds). Evict so
      // the next attempt with corrected creds gets a fresh pool. There is
      // no keychain entry to touch: nothing was registered in this flow.
      void dbDeleteDsn(handle);
      // Remember the handle so a retry with different creds reuses it —
      // keeps the pool-cache footprint bounded to 1 entry per wizard.
      setTestHandle(handle);
      setErrorMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setTestingConnection(false);
    }
  }

  // Save-to-BE — keychain write FIRST (``dbRegisterDsn`` mints the real
  // handle), then POST, then roll back on BE failure. Keychain-first is
  // the safer ordering because a stale keychain entry the BE never
  // learned about is inert; a live BE row without a keychain entry would
  // fail every subsequent query with a PERMISSION ack the user can't act
  // on (they'd have to reopen the wizard AND the BE-side connector row).
  async function handleSaveToBackend(): Promise<void> {
    if (!testHandle || !dialect || displayName.trim().length === 0) return;
    setErrorMessage(null);
    setSavingBe(true);

    // ``bridge_device_handle`` is only known once Rust has minted it —
    // hoisted so the catch block can roll back precisely that handle
    // (and skip the rollback when registration itself failed).
    let registeredHandle: string | null = null;
    try {
      const { bridge_device_handle, profile_fingerprint } = await dbRegisterDsn(buildDsn(dialect));
      registeredHandle = bridge_device_handle;

      const body: BridgeConnectorCreateRequest = {
        dialect,
        bridge_device_handle,
        profile_fingerprint,
        display_name: displayName.trim(),
      };
      const row = await apiClient.post<BridgeConnectorCreateResponse>(
        "/enterprise/connectors/bridge",
        body,
      );
      // Mark BEFORE closing so the dismiss interceptor / unmount
      // teardown treat this as a plain close. The step-2 test pool lives
      // under ``testHandle`` (never the registered handle), so evict it
      // here — the first BE query builds the live pool from the keychain.
      setSavedToBe(true);
      void dbDeleteDsn(testHandle);
      onRegistered?.(row);
      onOpenChange(false);
    } catch (err) {
      // BE POST failed (or registration itself did, in which case there
      // is nothing to roll back). Purge the keychain entry + Rust pool for
      // the freshly minted handle so the canonical answer to "does this
      // handle exist" is a single "no" everywhere — no half-committed
      // state to reason about on the next attempt.
      if (registeredHandle) {
        void dbDeleteDsn(registeredHandle);
      }
      setErrorMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingBe(false);
    }
  }

  const stepTitle: Record<WizardStep, string> = {
    1: t`Scegli il tipo di database`,
    2: t`Inserisci le credenziali`,
    3: t`Conferma la connessione`,
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2">
              <Database aria-hidden strokeWidth={1.75} className="h-4 w-4" />
              <Trans>Collega un database on-prem</Trans>
            </span>
          </DialogTitle>
          <DialogDescription>
            <span className="inline-flex items-start gap-1.5 text-xs">
              <ShieldCheck
                aria-hidden
                strokeWidth={1.75}
                className="mt-0.5 h-3.5 w-3.5 shrink-0"
              />
              <Trans>
                Le credenziali restano sul tuo dispositivo, nel portachiavi di
                sistema (Keychain). Il cloud Ando riceve solo un identificativo
                opaco per inoltrare le query.
              </Trans>
            </span>
          </DialogDescription>
        </DialogHeader>

        {!desktopAvailable ? (
          <div className="flex items-start gap-2 rounded-md border border-hairline bg-card p-3 text-xs text-ink-2">
            <TriangleAlert aria-hidden strokeWidth={1.75} className="mt-0.5 h-4 w-4 shrink-0" />
            <Trans>
              La configurazione del bridge è disponibile solo dall'app desktop
              Ando. Apri l'app sul computer che ha accesso al database.
            </Trans>
          </div>
        ) : (
          <>
            <StepIndicator step={step} label={stepTitle[step]} />

            {step === 1 ? (
              <div className="flex flex-col gap-2">
                <p className="text-xs text-ink-2">
                  <Trans>
                    Scegli il motore del database — il resto della procedura
                    cambia in base al dialetto.
                  </Trans>
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {DIALECTS.map((option) => (
                    <DialectRadio
                      key={option.value}
                      value={option.value}
                      label={DIALECT_LABELS[option.value]}
                      defaultPort={option.defaultPort}
                      checked={dialect === option.value}
                      onSelect={() => setDialect(option.value)}
                    />
                  ))}
                </div>
              </div>
            ) : null}

            {step === 2 && dialect !== null ? (
              <form
                className="flex flex-col gap-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  void handleTestAndRegister();
                }}
              >
                <div className="grid grid-cols-3 gap-2">
                  <label className="col-span-2 flex flex-col gap-1">
                    <span className="text-xs font-medium text-ink-2">
                      <Trans>Host</Trans>
                    </span>
                    <input
                      type="text"
                      required
                      className={inputCls}
                      value={credentials.host}
                      placeholder={t`db.azienda.local`}
                      onChange={(e) =>
                        setCredentials((prev) => ({ ...prev, host: e.target.value }))
                      }
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-ink-2">
                      <Trans>Porta</Trans>
                    </span>
                    <input
                      type="number"
                      required
                      className={inputCls}
                      value={credentials.port}
                      inputMode="numeric"
                      onChange={(e) =>
                        setCredentials((prev) => ({ ...prev, port: e.target.value }))
                      }
                    />
                  </label>
                </div>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-ink-2">
                    <Trans>Database</Trans>
                  </span>
                  <input
                    type="text"
                    required
                    className={inputCls}
                    value={credentials.database}
                    placeholder={t`production`}
                    onChange={(e) =>
                      setCredentials((prev) => ({ ...prev, database: e.target.value }))
                    }
                  />
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-ink-2">
                      <Trans>Utente</Trans>
                    </span>
                    <input
                      type="text"
                      required
                      autoComplete="off"
                      className={inputCls}
                      value={credentials.user}
                      onChange={(e) =>
                        setCredentials((prev) => ({ ...prev, user: e.target.value }))
                      }
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-ink-2">
                      <Lock aria-hidden strokeWidth={1.75} className="h-3 w-3" />
                      <Trans>Password</Trans>
                    </span>
                    {/* Reveal toggle sits inside the input row so it lines up
                        with the trailing edge without a separate flex track.
                        ``autoComplete="new-password"`` still tells browsers
                        this is a fresh credential (not a login) so the reveal
                        toggle doesn't fight the built-in unmask heuristics. */}
                    <div className="relative">
                      <input
                        type={showPw ? "text" : "password"}
                        required
                        autoComplete="new-password"
                        className={`${inputCls} pr-8`}
                        value={credentials.password}
                        onChange={(e) =>
                          setCredentials((prev) => ({ ...prev, password: e.target.value }))
                        }
                      />
                      <button
                        type="button"
                        onClick={() => setShowPw((v) => !v)}
                        aria-label={
                          showPw ? t`Nascondi password` : t`Mostra password`
                        }
                        className="-translate-y-1/2 absolute top-1/2 right-1 inline-flex h-6 w-6 items-center justify-center rounded text-ink-3 transition-colors hover:bg-black/[0.04] hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
                      >
                        {showPw ? (
                          <EyeOff aria-hidden strokeWidth={1.75} className="h-3.5 w-3.5" />
                        ) : (
                          <Eye aria-hidden strokeWidth={1.75} className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                  </label>
                </div>

                <AdvancedOptionsPanel
                  dialect={dialect}
                  open={advancedOpen}
                  onToggle={() => setAdvancedOpen((v) => !v)}
                  state={advancedOptions}
                  onChange={setAdvancedOptions}
                />

                {errorMessage ? (
                  <p className="inline-flex items-start gap-1.5 text-[12px] text-warning">
                    <TriangleAlert
                      aria-hidden
                      strokeWidth={1.75}
                      className="mt-0.5 h-3.5 w-3.5 shrink-0"
                    />
                    <span>{errorMessage}</span>
                  </p>
                ) : null}

                {/* Submit is inside the form so hitting Enter fires it. */}
                <input type="submit" hidden aria-hidden tabIndex={-1} />
              </form>
            ) : null}

            {step === 3 && dialect !== null && testHandle !== null ? (
              <div className="flex flex-col gap-3">
                <div className="inline-flex items-start gap-2 rounded-md border border-hairline bg-card px-3 py-2 text-xs text-ink-1">
                  <Check
                    aria-hidden
                    strokeWidth={2}
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600"
                  />
                  <span>
                    {/* Brand names aren't translated — render raw
                        (memory rule ``feedback_client_ui_strings_must_use_lingui``
                        exempts trademarks); wrapping in ``i18n._`` here
                        made Lingui try to look up "PostgreSQL" as a
                        catalog key). */}
                    <Trans>
                      Connessione verificata — {DIALECT_LABELS[dialect]} raggiungibile
                      da questa macchina.
                    </Trans>
                  </span>
                </div>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-ink-2">
                    <Trans>Nome visualizzato</Trans>
                  </span>
                  <input
                    type="text"
                    required
                    maxLength={80}
                    className={inputCls}
                    value={displayName}
                    placeholder={t`Es. ERP produzione`}
                    onChange={(e) => setDisplayName(e.target.value)}
                  />
                </label>
                {errorMessage ? (
                  <p className="inline-flex items-start gap-1.5 text-[12px] text-warning">
                    <TriangleAlert
                      aria-hidden
                      strokeWidth={1.75}
                      className="mt-0.5 h-3.5 w-3.5 shrink-0"
                    />
                    <span>{errorMessage}</span>
                  </p>
                ) : null}
              </div>
            ) : null}
          </>
        )}

        <DialogFooter>
          {step === 1 ? (
            <>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                <Trans>Annulla</Trans>
              </Button>
              <Button
                type="button"
                disabled={dialect === null || !desktopAvailable}
                onClick={() => setStep(2)}
              >
                <Trans>Avanti</Trans>
              </Button>
            </>
          ) : null}

          {step === 2 ? (
            <>
              <Button
                type="button"
                variant="ghost"
                disabled={testingConnection}
                onClick={() => {
                  setErrorMessage(null);
                  setStep(1);
                }}
              >
                <ArrowLeft aria-hidden strokeWidth={1.75} />
                <Trans>Indietro</Trans>
              </Button>
              <Button
                type="button"
                disabled={!credentialsReady || testingConnection}
                onClick={() => void handleTestAndRegister()}
              >
                {testingConnection ? (
                  <>
                    <Loader2 aria-hidden strokeWidth={1.75} className="animate-spin" />
                    <Trans>Verifica…</Trans>
                  </>
                ) : (
                  <Trans>Verifica e continua</Trans>
                )}
              </Button>
            </>
          ) : null}

          {step === 3 ? (
            <>
              <Button
                type="button"
                variant="ghost"
                disabled={savingBe}
                onClick={() => {
                  // Let the user tweak the creds; we keep the tested handle
                  // around so a re-test isn't strictly necessary if nothing
                  // changed, but the retest button on step 2 will overwrite
                  // it if they retry.
                  setErrorMessage(null);
                  setTestedOk(false);
                  setStep(2);
                }}
              >
                <ArrowLeft aria-hidden strokeWidth={1.75} />
                <Trans>Indietro</Trans>
              </Button>
              <Button
                type="button"
                disabled={
                  !testedOk ||
                  savingBe ||
                  displayName.trim().length === 0 ||
                  testHandle === null
                }
                onClick={() => void handleSaveToBackend()}
              >
                {savingBe ? (
                  <>
                    <Loader2 aria-hidden strokeWidth={1.75} className="animate-spin" />
                    <Trans>Salvo…</Trans>
                  </>
                ) : (
                  <Trans>Salva</Trans>
                )}
              </Button>
            </>
          ) : null}
        </DialogFooter>
      </DialogContent>

      {/* Unsaved-changes confirm — kept as a sibling of the primary
          Dialog rather than nested inside ``DialogContent`` so Radix'
          focus-trap on the wizard doesn't clash with the AlertDialog's
          own trap. It only mounts while the user is being asked; the
          primary Dialog stays open behind it either way. */}
      <AlertDialog
        open={confirmingDismiss}
        onOpenChange={(next) => {
          if (!next) setConfirmingDismiss(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Chiudere senza salvare?</Trans>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>
                Le credenziali inserite andranno perse. Nessuna informazione
                è stata inviata al cloud Ando.
              </Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <Trans>Continua a compilare</Trans>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                // Prevent Radix' default close-on-action so we control
                // the sequence: evict the test pool first, then close the
                // primary Dialog, then let the AlertDialog fold up.
                e.preventDefault();
                setConfirmingDismiss(false);
                closeAndCleanup();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <Trans>Chiudi comunque</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};
EnterpriseDbBridgeSetupWizard.displayName = "EnterpriseDbBridgeSetupWizard";

// ── Small pieces ────────────────────────────────────────────────────

function StepIndicator({
  step,
  label,
}: {
  step: WizardStep;
  label: string;
}): ReactElement {
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-1" aria-hidden>
        {([1, 2, 3] as const).map((s) => (
          <span
            key={s}
            className={
              s <= step
                ? "h-1 w-6 rounded-full bg-ando-orange transition-colors duration-150 ease-out"
                : "h-1 w-6 rounded-full bg-hairline transition-colors duration-150 ease-out"
            }
          />
        ))}
      </div>
      <span className="text-xs text-ink-3">
        <Trans>
          Passaggio {step} di 3 — {label}
        </Trans>
      </span>
    </div>
  );
}

/**
 * Advanced options panel — a plain button-toggled section (``@ando/ui``
 * has no ``Collapsible`` / ``Accordion`` primitive at time of writing;
 * see ``packages/ui/src/primitives/index.ts``). Kept as a controlled
 * component with ``open`` + ``onToggle`` so the parent owns the
 * open/closed bit and can reset it alongside the rest of the wizard
 * state on dialog reopen.
 *
 * Only the fields belonging to the currently-selected ``dialect`` are
 * rendered — a per-dialect switch below — so the panel never surfaces
 * knobs the caller couldn't act on.
 */
function AdvancedOptionsPanel({
  dialect,
  open,
  onToggle,
  state,
  onChange,
}: {
  dialect: EnterpriseDbDialect;
  open: boolean;
  onToggle: () => void;
  state: AdvancedOptionsState;
  onChange: (next: AdvancedOptionsState) => void;
}): ReactElement {
  const bodyId = "enterprise-db-bridge-advanced-body";
  const patch = (delta: Partial<AdvancedOptionsState>): void => {
    onChange({ ...state, ...delta });
  };
  return (
    <div className="flex flex-col gap-2 rounded-md border border-hairline bg-card px-3 py-2">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={bodyId}
        className="-mx-1 inline-flex items-center gap-1.5 rounded px-1 py-0.5 text-left text-xs font-medium text-ink-2 transition-colors hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {open ? (
          <ChevronDown aria-hidden strokeWidth={1.75} className="h-3.5 w-3.5" />
        ) : (
          <ChevronRight aria-hidden strokeWidth={1.75} className="h-3.5 w-3.5" />
        )}
        <Settings2 aria-hidden strokeWidth={1.75} className="h-3.5 w-3.5" />
        <Trans>Opzioni avanzate</Trans>
      </button>
      {open ? (
        <div id={bodyId} className="flex flex-col gap-3 pt-1">
          {dialect === "postgresql" ? (
            <PostgresAdvancedFields state={state} patch={patch} />
          ) : null}
          {dialect === "mssql" ? (
            <MssqlAdvancedFields state={state} patch={patch} />
          ) : null}
          {dialect === "mysql" ? (
            <MysqlAdvancedFields state={state} patch={patch} />
          ) : null}
          {dialect === "oracle" ? (
            <OracleAdvancedFields state={state} patch={patch} />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// Shared classes for the advanced-options controls. Kept module-scope so
// each field-cluster component below doesn't re-declare them per render.
const selectCls =
  "w-full rounded-md border border-hairline bg-card px-2.5 py-1.5 text-sm text-ink-1 focus:outline-none focus:ring-2 focus:ring-ring";
const fieldLabelCls = "text-xs font-medium text-ink-2";
const fieldHintCls = "text-[11px] text-ink-3";

function PostgresAdvancedFields({
  state,
  patch,
}: {
  state: AdvancedOptionsState;
  patch: (delta: Partial<AdvancedOptionsState>) => void;
}): ReactElement {
  const { t } = useLingui();
  const showRootCert =
    state.pgSslMode === "verify-ca" || state.pgSslMode === "verify-full";
  const timeoutInvalid = !isPositiveIntOrBlank(state.pgConnectTimeout);
  return (
    <>
      <label className="flex flex-col gap-1">
        <span className={fieldLabelCls}>
          <Trans>Modalità SSL</Trans>
        </span>
        <StyledSelect
          className={selectCls}
          value={state.pgSslMode}
          onChange={(e) => patch({ pgSslMode: e.target.value as PgSslMode })}
        >
          {PG_SSLMODES.map((mode) => (
            <option key={mode} value={mode}>
              {mode}
            </option>
          ))}
        </StyledSelect>
      </label>
      {showRootCert ? (
        <label className="flex flex-col gap-1">
          <span className={fieldLabelCls}>
            <Trans>Certificato root SSL</Trans>
          </span>
          <input
            type="text"
            className={inputCls}
            value={state.pgSslRootCert}
            placeholder={t`/percorso/al/ca.pem`}
            onChange={(e) => patch({ pgSslRootCert: e.target.value })}
          />
          <span className={fieldHintCls}>
            <Trans>Percorso del CA o contenuto PEM.</Trans>
          </span>
        </label>
      ) : null}
      <label className="flex flex-col gap-1">
        <span className={fieldLabelCls}>
          <Trans>Timeout connessione (s)</Trans>
        </span>
        <input
          type="number"
          min={1}
          inputMode="numeric"
          className={inputCls}
          value={state.pgConnectTimeout}
          placeholder={t`10`}
          onChange={(e) => patch({ pgConnectTimeout: e.target.value })}
          aria-invalid={timeoutInvalid || undefined}
        />
        {timeoutInvalid ? (
          <span className="text-[11px] text-warning">
            <Trans>Inserire un numero intero positivo.</Trans>
          </span>
        ) : null}
      </label>
    </>
  );
}

function MssqlAdvancedFields({
  state,
  patch,
}: {
  state: AdvancedOptionsState;
  patch: (delta: Partial<AdvancedOptionsState>) => void;
}): ReactElement {
  const { t } = useLingui();
  const timeoutInvalid = !isPositiveIntOrBlank(state.mssqlConnectionTimeout);
  return (
    <>
      <label className="inline-flex items-center gap-2 text-xs text-ink-1">
        <input
          type="checkbox"
          checked={state.mssqlTrustServerCertificate}
          onChange={(e) =>
            patch({ mssqlTrustServerCertificate: e.target.checked })
          }
          className="h-3.5 w-3.5 rounded border-hairline text-ando-orange focus:ring-ring"
        />
        <Trans>Fidati del certificato del server</Trans>
      </label>
      <label className="flex flex-col gap-1">
        <span className={fieldLabelCls}>
          <Trans>Cifratura</Trans>
        </span>
        <StyledSelect
          className={selectCls}
          value={state.mssqlEncrypt}
          onChange={(e) =>
            patch({ mssqlEncrypt: e.target.value as MssqlEncrypt })
          }
        >
          {MSSQL_ENCRYPT.map((mode) => (
            <option key={mode} value={mode}>
              {mode}
            </option>
          ))}
        </StyledSelect>
      </label>
      <label className="flex flex-col gap-1">
        <span className={fieldLabelCls}>
          <Trans>Timeout connessione (s)</Trans>
        </span>
        <input
          type="number"
          min={1}
          inputMode="numeric"
          className={inputCls}
          value={state.mssqlConnectionTimeout}
          placeholder={t`15`}
          onChange={(e) => patch({ mssqlConnectionTimeout: e.target.value })}
          aria-invalid={timeoutInvalid || undefined}
        />
        {timeoutInvalid ? (
          <span className="text-[11px] text-warning">
            <Trans>Inserire un numero intero positivo.</Trans>
          </span>
        ) : null}
      </label>
    </>
  );
}

function MysqlAdvancedFields({
  state,
  patch,
}: {
  state: AdvancedOptionsState;
  patch: (delta: Partial<AdvancedOptionsState>) => void;
}): ReactElement {
  const { t } = useLingui();
  const timeoutInvalid = !isPositiveIntOrBlank(state.mysqlConnectTimeout);
  return (
    <>
      <label className="flex flex-col gap-1">
        <span className={fieldLabelCls}>
          <Trans>Modalità SSL</Trans>
        </span>
        <StyledSelect
          className={selectCls}
          value={state.mysqlSslMode}
          onChange={(e) =>
            patch({ mysqlSslMode: e.target.value as MysqlSslMode })
          }
        >
          {MYSQL_SSLMODES.map((mode) => (
            <option key={mode} value={mode}>
              {mode}
            </option>
          ))}
        </StyledSelect>
      </label>
      <label className="flex flex-col gap-1">
        <span className={fieldLabelCls}>
          <Trans>Timeout connessione (ms)</Trans>
        </span>
        <input
          type="number"
          min={1}
          inputMode="numeric"
          className={inputCls}
          value={state.mysqlConnectTimeout}
          placeholder={t`10000`}
          onChange={(e) => patch({ mysqlConnectTimeout: e.target.value })}
          aria-invalid={timeoutInvalid || undefined}
        />
        {timeoutInvalid ? (
          <span className="text-[11px] text-warning">
            <Trans>Inserire un numero intero positivo.</Trans>
          </span>
        ) : null}
      </label>
    </>
  );
}

function OracleAdvancedFields({
  state,
  patch,
}: {
  state: AdvancedOptionsState;
  patch: (delta: Partial<AdvancedOptionsState>) => void;
}): ReactElement {
  const { t } = useLingui();
  const timeoutInvalid = !isPositiveIntOrBlank(state.oracleConnectTimeout);
  return (
    <>
      <label className="flex flex-col gap-1">
        <span className={fieldLabelCls}>
          <Trans>Service name</Trans>
        </span>
        <input
          type="text"
          className={inputCls}
          value={state.oracleServiceName}
          placeholder={t`ORCLPDB1`}
          onChange={(e) => patch({ oracleServiceName: e.target.value })}
        />
        <span className={fieldHintCls}>
          <Trans>Alternativa al SID; opzionale.</Trans>
        </span>
      </label>
      <label className="flex flex-col gap-1">
        <span className={fieldLabelCls}>
          <Trans>Timeout connessione (s)</Trans>
        </span>
        <input
          type="number"
          min={1}
          inputMode="numeric"
          className={inputCls}
          value={state.oracleConnectTimeout}
          placeholder={t`30`}
          onChange={(e) => patch({ oracleConnectTimeout: e.target.value })}
          aria-invalid={timeoutInvalid || undefined}
        />
        {timeoutInvalid ? (
          <span className="text-[11px] text-warning">
            <Trans>Inserire un numero intero positivo.</Trans>
          </span>
        ) : null}
      </label>
    </>
  );
}

function DialectRadio({
  value,
  label,
  defaultPort,
  checked,
  onSelect,
}: {
  value: EnterpriseDbDialect;
  label: string;
  defaultPort: number;
  checked: boolean;
  onSelect: () => void;
}): ReactElement {
  return (
    <label
      className={
        checked
          ? "flex cursor-pointer items-start gap-2.5 rounded-md border border-ando-orange bg-ando-orange/[0.08] px-2.5 py-2 transition-colors duration-150 ease-out focus-within:ring-2 focus-within:ring-ring"
          : "flex cursor-pointer items-start gap-2.5 rounded-md border border-hairline bg-card px-2.5 py-2 transition-colors duration-150 ease-out hover:bg-black/[0.04] focus-within:ring-2 focus-within:ring-ring"
      }
    >
      <input
        type="radio"
        name="enterprise-db-bridge-dialect"
        className="sr-only"
        value={value}
        checked={checked}
        onChange={onSelect}
      />
      <span
        aria-hidden
        className={
          checked
            ? "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-ando-orange"
            : "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-hairline"
        }
      >
        {checked ? <span className="h-2 w-2 rounded-full bg-ando-orange" /> : null}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-ink-1 leading-5">{label}</span>
        <span className="block text-xs text-ink-3 leading-4">
          <Trans>Porta di default {defaultPort}</Trans>
        </span>
      </span>
    </label>
  );
}
